import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowRight, BookOpen, Check, CheckCircle2, ChevronDown, CornerDownLeft, ExternalLink, FileText, Flag, FolderPlus, ListChecks, Loader2, Mail,
  MessageSquare, Receipt, RefreshCw, Reply, Send, Tag, X,
} from 'lucide-react';
import { apiService } from '../../services/api';
import NexusBot from '../Exploration/nexus/NexusBot';
import { DeadlineChip } from './ProjectCards';
import useLivePolling from './useLivePolling';

// Muse lit les nouveaux mails (Gmail, lecture seule) et propose quoi en faire : tâche, point à aborder, note de
// journal, réponse attendue, facture à ajouter en dépense. Rien n'est fait sans clic, et aucun mail n'est conservé :
// seules ses propositions. Vivant : la carte se met à jour seule, et ce qui a été traité dans Gmail (réponse envoyée,
// transfert, réponse d'un collègue, mail supprimé) disparaît. Le projet d'un mail se corrige d'un clic (retenu pour la
// suite du fil et de l'expéditeur), et on peut parler à Nexus d'un mail : une précision, une tâche qu'il n'a pas
// proposée. Ce qui est à traiter vient d'abord ; « pour info » est replié ; un mail s'écarte d'un clic (Nexus s'en
// souvient, comme de chaque « pas utile »). Les mails envoyés aussi : ce qu'ils font avancer (offre partie à noter au
// journal, tâche à cocher, objectif à clore, version d'une proposition de Deepflow à nommer « Envoyée par … ») a sa
// carte « À … », à part du fil reçu.

const KINDS = {
  task: { icon: ListChecks, action: 'Créer la tâche', done: 'Tâche créée' },
  point: { icon: MessageSquare, action: 'Ajouter aux points', done: 'Point ajouté' },
  journal: { icon: BookOpen, action: 'Noter au journal', done: 'Noté au journal' },
  reply: { icon: Reply, action: 'Répondre', done: 'Ouvert dans Gmail' },
  expense: { icon: Receipt, action: 'Ajouter en dépense', done: 'Facture ajoutée aux dépenses' },
  project: { icon: FolderPlus, action: 'Créer le projet', done: 'Projet créé (en proposition)' },
  close: { icon: CheckCircle2, action: 'Clôturer la tâche', done: 'Tâche terminée' },
  objective: { icon: Flag, action: "Clore l'objectif", done: 'Objectif terminé' },
  offer: { icon: Tag, action: 'Nommer la version', done: 'Version nommée' },
};
const POLL_MS = 30000;
const READING_POLL_MS = 6000; // pendant que Muse lit de nouveaux mails
const HANDLED_MS = 8000;

const cardKey = (mail) => encodeURIComponent(mail.key || mail.thread_id || mail.message_id);
const gmailLink = (mail) => `https://mail.google.com/mail/u/0/#all/${mail.thread_id || mail.message_id}`;

const receivedAt = (iso, today) => {
  if (!iso) return '';
  const date = new Date(`${iso.replace(' ', 'T')}${iso.endsWith('Z') ? '' : 'Z'}`);
  return iso.slice(0, 10) === today
    ? date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};
const DONE_MS = 6000; // l'action réussie reste affichée, avec son lien, avant de laisser la place

// Où retrouver ce qui vient d'être créé.
const resultLink = (result) => {
  if (result.kind === 'task' || result.kind === 'close') return { href: '/backlog', label: 'Tâches' };
  if (result.kind === 'expense') return { href: '/expenses', label: 'Voir la dépense' };
  if (result.kind === 'project' && result.created_id) return { href: `/project/${result.created_id}`, label: 'Ouvrir le projet' };
  if (result.kind === 'journal' && result.project_id) {
    return { href: `/project/${result.project_id}?panel=journal&entryId=${result.created_id}`, label: 'Voir la note' };
  }
  if (result.kind === 'offer' && result.created_id) {
    const label = result.url?.startsWith('/tools/presentations/') ? 'Voir la présentation'
      : result.url?.startsWith('/tools/rapports/') ? 'Voir le rapport' : 'Voir la proposition';
    return { href: result.url || `/tools/offres/propositions/${result.created_id}`, label };
  }
  if ((result.kind === 'point' || result.kind === 'objective') && result.project_id) return { href: `/project/${result.project_id}`, label: 'Voir le projet' };
  return null;
};

const Proposal = ({ proposal, mail, projects, today, onDone, onError, onCreated }) => {
  const kind = KINDS[proposal.kind] || KINDS.task;
  const Icon = kind.icon;
  const needsProject = (proposal.kind === 'journal' || (proposal.kind === 'point' && !mail.client_id)) && !mail.project_id;
  const [projectId, setProjectId] = useState('');
  const [instruction, setInstruction] = useState(''); // facture : à quels projets, en une phrase
  const [label, setLabel] = useState(proposal.label || ''); // offre envoyée : nom de la version, modifiable
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // action faite : on la montre avant de retirer la proposition
  const [leaving, setLeaving] = useState(false);

  const leave = (delay) => setTimeout(() => {
    setLeaving(true);
    setTimeout(() => onDone(proposal.id), 250);
  }, delay);

  const act = async (accept) => {
    setBusy(true);
    try {
      if (accept) {
        if (proposal.kind === 'reply') window.open(gmailLink(mail), '_blank', 'noopener');
        const body = proposal.kind === 'expense'
          ? (instruction.trim() ? { instruction: instruction.trim() } : {})
          : proposal.kind === 'offer' ? { detail: label.trim() }
            : (projectId ? { project_id: Number(projectId) } : {});
        const done = await apiService.post(`/home/mail/proposals/${proposal.id}/accept`, body);
        setResult({ ...done, project_id: done.project_id || (projectId ? Number(projectId) : mail.project_id) });
        if ((done.kind === 'task' || done.kind === 'close') && onCreated) onCreated();
        leave(DONE_MS);
      } else {
        await apiService.post(`/home/mail/proposals/${proposal.id}/dismiss`, {});
        leave(0);
      }
    } catch (err) {
      onError(err.message);
      setBusy(false);
    }
  };

  if (proposal.done_by && !result) {
    // Un collègue qui a reçu le même mail l'a déjà fait : on le dit, sans bouton (pas de doublon, pas d'oubli).
    const by = proposal.done_by;
    const link = resultLink({ kind: proposal.kind, created_id: by.created_id, project_id: by.project_id || mail.project_id, url: proposal.url });
    return (
      <div className={`group flex items-start gap-2.5 rounded-xl px-2 py-1.5 transition-all duration-200 ${leaving ? '-translate-x-2 opacity-0' : ''}`}>
        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600"><Check size={10} /></span>
        <div className="min-w-0 flex-1">
          <p className="text-sm leading-snug text-slate-500 line-through decoration-slate-300">{proposal.title}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-emerald-700">
            <span><span className="font-semibold">{kind.done}</span> par {(by.employee_name || 'un collègue').split(' ')[0]}</span>
            {link && (
              <a href={link.href} className="flex items-center gap-0.5 font-semibold hover:underline">{link.label} <ArrowRight size={11} /></a>
            )}
          </p>
        </div>
        <button type="button" title="Masquer" onClick={() => act(false)} disabled={busy}
          className="rounded-md p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-500"><X size={14} /></button>
      </div>
    );
  }

  if (result) {
    const link = resultLink(result);
    return (
      <div className={`flex items-center gap-2.5 rounded-xl bg-emerald-50 px-2 py-2 text-sm text-emerald-800 transition-all duration-300 animate-muse-line-in ${leaving ? 'opacity-0' : 'opacity-100'}`}>
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white"><Check size={12} /></span>
        <span className="min-w-0 flex-1 truncate"><span className="font-semibold">{kind.done}</span> · {proposal.title}</span>
        {link && (
          <a href={link.href} className="flex shrink-0 items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline">
            {link.label} <ArrowRight size={12} />
          </a>
        )}
      </div>
    );
  }

  return (
    <div className={`group flex items-start gap-2.5 rounded-xl px-2 py-1.5 transition-all duration-200 hover:bg-slate-50 ${leaving ? '-translate-x-2 opacity-0' : ''} ${busy ? 'opacity-60' : ''}`}>
      <Icon size={15} className="mt-0.5 shrink-0 text-indigo-400" />
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-snug text-slate-800">{proposal.title}</p>
        {proposal.kind === 'expense' ? (
          <div className="mt-1 flex flex-wrap gap-1">
            {(proposal.files || []).map((name) => (
              <span key={name} className="flex max-w-full items-center gap-1 truncate rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
                <FileText size={11} className="shrink-0 text-slate-400" /> {name}
              </span>
            ))}
            {(proposal.receipts || []).map((name) => (
              <span key={`recu-${name}`} title="Reçu du même paiement : gardé avec la facture comme justificatif, pas une dépense de plus"
                className="flex max-w-full items-center gap-1 truncate rounded-md border border-dashed border-slate-200 px-1.5 py-0.5 text-[11px] text-slate-500">
                <Receipt size={11} className="shrink-0 text-slate-400" /> Reçu · {name}
              </span>
            ))}
          </div>
        ) : proposal.detail && <p className="mt-0.5 line-clamp-2 text-xs text-slate-500" title={proposal.detail}>{proposal.detail}</p>}
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {proposal.kind === 'expense' && (
            <input value={instruction} onChange={(e) => setInstruction(e.target.value)} disabled={busy}
              onKeyDown={(e) => { if (e.key === 'Enter') act(true); }}
              placeholder="Projets ? ex. « tout sur Olista 70 »"
              className="min-w-[12rem] flex-1 rounded-lg border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-700 placeholder:text-slate-400 focus:border-indigo-300 focus:outline-none" />
          )}
          {proposal.kind === 'offer' && (
            <input value={label} onChange={(e) => setLabel(e.target.value)} disabled={busy} maxLength={100}
              onKeyDown={(e) => { if (e.key === 'Enter' && label.trim()) act(true); }}
              aria-label="Nom de la version"
              className="min-w-[16rem] flex-1 rounded-lg border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-700 focus:border-indigo-300 focus:outline-none" />
          )}
          {proposal.kind === 'offer' && (proposal.url || proposal.proposal_id) && (
            <a href={proposal.url || `/tools/offres/propositions/${proposal.proposal_id}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-0.5 text-xs text-slate-500 hover:text-indigo-700">
              Ouvrir <ExternalLink size={11} />
            </a>
          )}
          {proposal.due_date && <DeadlineChip today={today} deadline={{ date: proposal.due_date, label: 'Échéance' }} />}
          {needsProject && (
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)}
              className="max-w-[12rem] rounded-lg border border-slate-200 bg-white px-1.5 py-0.5 text-xs text-slate-600">
              <option value="">Projet…</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <button type="button" onClick={() => act(true)}
            disabled={busy || (needsProject && !projectId) || (proposal.kind === 'offer' && !label.trim())}
            className="flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700 hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-40">
            {busy && <Loader2 size={11} className="animate-spin" />}{kind.action}
          </button>
        </div>
      </div>
      <button type="button" title="Pas utile" onClick={() => act(false)} disabled={busy}
        className="rounded-md p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-500"><X size={14} /></button>
    </div>
  );
};

/** Le projet d'un mail : un clic pour le corriger (ou n'en mettre aucun). */
const ProjectPicker = ({ mail, projects, mine, onChange }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const label = mail.project_name || mail.client_name;
  const choose = async (value) => {
    setOpen(false);
    setBusy(true);
    await onChange(value === '' ? null : Number(value));
    setBusy(false);
  };
  if (open) {
    const ids = new Set((mine || []).map((p) => p.id));
    const sorted = [...(projects || [])].sort((a, b) => (ids.has(b.id) - ids.has(a.id)) || a.name.localeCompare(b.name));
    return (
      <select autoFocus value={mail.project_id || ''} onChange={(e) => choose(e.target.value)} onBlur={() => setOpen(false)}
        onClick={(e) => e.stopPropagation()}
        className="max-w-full rounded-lg border border-indigo-200 bg-white px-1.5 py-0.5 text-[11px] text-slate-700 focus:outline-none">
        <option value="">Aucun projet</option>
        {sorted.map((p) => <option key={p.id} value={p.id}>{p.name}{p.client ? ` · ${p.client}` : ''}</option>)}
      </select>
    );
  }
  return (
    <button type="button" title="Changer le projet de ce mail"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
      className={`group/project flex max-w-full shrink-0 items-center gap-0.5 truncate rounded-full px-2 py-0.5 text-[10px] font-medium transition ${label ? 'bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700' : 'border border-dashed border-slate-300 text-slate-400 hover:border-indigo-300 hover:text-indigo-600'}`}>
      {busy ? <Loader2 size={10} className="animate-spin" /> : null}
      <span className="truncate">{label || 'Projet ?'}</span>
      <ChevronDown size={10} className="shrink-0 opacity-50 group-hover/project:opacity-100" />
    </button>
  );
};

/** Parler à Nexus d'un mail : une précision, une tâche qu'il n'a pas proposée ; il relit le mail et propose. */
const AskNexus = ({ mail, onAnswer, onError }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState(null);
  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setReply(null);
    try {
      const done = await apiService.post(`/home/mail/threads/${cardKey(mail)}/ask`, { text: text.trim() });
      setReply(done.message);
      setText('');
      onAnswer(done.mails);
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} title="Préciser ou demander quelque chose à Nexus sur ce mail"
        className="flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold text-indigo-500 opacity-70 transition hover:bg-indigo-50 hover:opacity-100">
        <NexusBot mood="idle" size={16} /> Nexus
      </button>
    );
  }
  return (
    <div className="mt-1.5 w-full rounded-xl bg-indigo-50/70 p-1.5 ring-1 ring-indigo-100 animate-muse-line-in">
      {reply && (
        <p className="mb-1.5 flex items-start gap-1.5 px-1 text-xs leading-snug text-slate-700">
          <NexusBot mood="happy" size={18} className="-mt-0.5 shrink-0" /> <span>{reply}</span>
        </p>
      )}
      <form className="flex items-center gap-1.5" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <NexusBot mood={busy ? 'writing' : 'waiting'} size={20} className="shrink-0" />
        <input autoFocus value={text} onChange={(e) => setText(e.target.value)} disabled={busy}
          placeholder={busy ? 'Nexus relit le mail…' : 'Ex. « crée une tâche pour relancer Samuel lundi », « c’est pour InKurve »'}
          className="min-w-0 flex-1 rounded-lg border border-indigo-100 bg-white px-2 py-1 text-xs text-slate-700 placeholder:text-slate-400 focus:border-indigo-300 focus:outline-none" />
        <button type="submit" disabled={busy || !text.trim()} title="Envoyer"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white disabled:opacity-40">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <CornerDownLeft size={12} />}
        </button>
        <button type="button" onClick={() => { setOpen(false); setReply(null); }} title="Fermer" className="shrink-0 text-slate-400 hover:text-slate-600"><X size={13} /></button>
      </form>
    </div>
  );
};

const handledText = (item) => {
  if (item.reason === 'replied') return `Déjà répondu à ${item.to || item.from_name}`;
  if (item.reason === 'forwarded') return `Mail de ${item.from_name} transféré${item.to ? ` à ${item.to}` : ''}`;
  if (item.reason === 'colleague') return `${item.by || 'Un collègue'} a répondu à ${item.to || item.from_name}`;
  return `Mail de ${item.from_name} supprimé`;
};

const INFO_OPEN_KEY = 'home.mail.infoOpen';
const readInfoOpen = () => {
  try { return window.localStorage.getItem(INFO_OPEN_KEY) === '1'; } catch { return false; }
};

/** Un mail (son fil) et ce que Nexus propose d'en faire ; « pour info » : plus discret. */
const MailItem = ({ mail, data, projects, today, muted, onDone, onError, onCreated, onProject, onAnswer, onDismiss }) => {
  const [leaving, setLeaving] = useState(false);
  const dismiss = async () => {
    setLeaving(true);
    try {
      await apiService.post(`/home/mail/threads/${cardKey(mail)}/dismiss`, {});
      setTimeout(() => onDismiss(mail), 200);
    } catch (err) {
      setLeaving(false);
      onError(err.message);
    }
  };
  return (
    <article className={`mb-4 min-w-0 break-inside-avoid rounded-2xl border p-3 transition-all duration-200 ${muted ? 'border-slate-200/60 bg-white/60' : 'border-slate-200/80 bg-white shadow-sm hover:border-slate-300'} ${leaving ? 'scale-[0.98] opacity-0' : ''}`}>
      <div className="relative">
        <div className="absolute right-0 top-0 flex max-w-[47%] items-center justify-end gap-0.5">
          <ProjectPicker mail={mail} projects={data.projects} mine={projects} onChange={(id) => onProject(mail, id)} />
          <button type="button" onClick={dismiss} title="Écarter ce mail (Nexus s'en souviendra)"
            className="shrink-0 rounded-md p-0.5 text-slate-300 transition hover:bg-slate-100 hover:text-slate-500">
            <X size={13} />
          </button>
        </div>
        <a href={gmailLink(mail)} target="_blank" rel="noreferrer" title="Ouvrir dans Gmail" className="group/mail block px-2 pb-1 pr-[49%]">
          <div className="flex items-center gap-2 text-xs">
            {(mail.direction === 'sent' || mail.direction === 'copy') && <Send size={11} className="shrink-0 text-indigo-400" aria-label="Mail envoyé" />}
            <span className={`min-w-0 truncate font-semibold ${muted ? 'text-slate-600' : 'text-slate-800'}`}>
              {mail.direction === 'sent' && <span className="font-normal text-slate-500">À </span>}{mail.from_name}
              {mail.direction === 'copy' && <span className="font-normal text-slate-500"> · en copie</span>}
            </span>
            <span className="shrink-0 text-slate-400">{receivedAt(mail.received_at, today)}</span>
          </div>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500 group-hover/mail:text-slate-800">
            <span className="truncate">{mail.subject}</span>
            <ExternalLink size={10} className="shrink-0 opacity-0 group-hover/mail:opacity-100" />
          </p>
        </a>
      </div>
      <div className="mt-1 border-t border-slate-100 pt-1">
        {mail.proposals.map((proposal) => (
          <Proposal key={proposal.id} proposal={proposal} mail={mail} projects={projects} today={today}
            onDone={onDone} onError={onError} onCreated={onCreated} />
        ))}
        <div className="flex flex-wrap justify-end px-1 pt-0.5">
          <AskNexus mail={mail} onError={onError} onAnswer={onAnswer} />
        </div>
      </div>
    </article>
  );
};

const MailCard = ({ today, projects, onTaskCreated }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [notice, setNotice] = useState(null);
  const [waiting, setWaiting] = useState([]); // nouveaux mails que Muse est en train de lire
  const [handled, setHandled] = useState([]); // traités dans Gmail entre-temps : retirés, on le dit un instant
  const [infoOpen, setInfoOpen] = useState(readInfoOpen); // « pour info » : replié par défaut
  const loadingRef = useRef(false);
  const toggleInfo = () => setInfoOpen((open) => {
    try { window.localStorage.setItem(INFO_OPEN_KEY, open ? '0' : '1'); } catch { /* stockage indisponible */ }
    return !open;
  });

  const load = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      const next = await apiService.get('/home/mail');
      setData(next);
      setWaiting((prev) => (next.waiting?.length ? next.waiting : next.reading ? prev : []));
      if (next.resolved?.length) {
        const stamp = Date.now();
        const same = (a, b) => a.from_name === b.from_name && a.subject === b.subject && a.reason === b.reason;
        setHandled((prev) => [...prev, ...next.resolved.filter((item) => !prev.some((p) => same(p, item)))
          .map((item, i) => ({ ...item, key: `${stamp}-${i}` }))]);
        setTimeout(() => setHandled((prev) => prev.filter((item) => !item.key.startsWith(`${stamp}-`))), HANDLED_MS);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      loadingRef.current = false;
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useLivePolling(load, data?.reading ? READING_POLL_MS : POLL_MS, { enabled: Boolean(data?.connected) });

  const connect = async () => {
    try {
      const { authorization_url: url } = await apiService.post('/home/mail/connect', { return_to: window.location.pathname });
      window.location.href = url;
    } catch (err) {
      setError(err.message);
    }
  };
  const scan = async () => {
    setScanning(true);
    try {
      const result = await apiService.post('/home/mail/scan', {});
      setData((prev) => ({ ...prev, mails: result.mails }));
      setNotice(result.read ? null : 'Pas de nouveau mail.');
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setScanning(false);
    }
  };
  const setProject = async (mail, projectId) => {
    try {
      const result = await apiService.put(`/home/mail/threads/${cardKey(mail)}/project`, { project_id: projectId });
      setData((prev) => ({ ...prev, mails: result.mails }));
    } catch (err) {
      setError(err.message);
    }
  };
  const done = (proposalId) => {
    setData((prev) => ({
      ...prev,
      mails: prev.mails.map((m) => ({ ...m, proposals: m.proposals.filter((p) => p.id !== proposalId) }))
        .filter((m) => m.proposals.length),
    }));
  };
  const dismissMail = (mail) => {
    setData((prev) => ({ ...prev, mails: prev.mails.filter((m) => (m.key || m.message_id) !== (mail.key || mail.message_id)) }));
  };

  if (!data) return null;
  const toDo = (data.mails || []).filter((m) => m.importance !== 'info');
  const forInfo = (data.mails || []).filter((m) => m.importance === 'info');
  const item = (mail, muted) => (
    <MailItem key={mail.key || mail.message_id} mail={mail} data={data} projects={projects} today={today} muted={muted}
      onDone={done} onError={setError} onCreated={onTaskCreated} onProject={setProject} onDismiss={dismissMail}
      onAnswer={(mails) => setData((prev) => ({ ...prev, mails }))} />
  );
  // Section pleine largeur, comme « Mes projets actifs » : une carte par mail, en grille.
  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Mail size={16} className="text-slate-400" /> Mes mails
          {data.connected && toDo.length > 0 && <span className="rounded-full bg-slate-200/70 px-2 text-xs font-medium text-slate-600">{toDo.length}</span>}
          {data.connected && <span className="hidden text-xs font-normal text-slate-400 sm:inline">· ce que Nexus propose d'en faire</span>}
        </h2>
        {data.connected && (
          <button type="button" onClick={scan} disabled={scanning} title="Nexus lit tout de suite les nouveaux mails"
            className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-white hover:text-slate-700">
            {scanning ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Relire
          </button>
        )}
      </div>
      {data.connected && (waiting.length > 0 || handled.length > 0) && (
        <div className="mb-3 flex flex-wrap gap-2">
          {data.reading && waiting.map((mail) => (
            <span key={mail.message_id} className="flex max-w-full items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1 text-xs text-indigo-700 animate-muse-line-in">
              <Loader2 size={11} className="shrink-0 animate-spin" />
              <span className="truncate">
                {mail.direction === 'sent' ? 'Nexus lit ton mail à ' : 'Nexus lit le mail de '}
                <span className="font-semibold">{mail.from_name}</span> · {mail.subject}
              </span>
            </span>
          ))}
          {handled.map((item) => (
            <span key={item.key} className="flex max-w-full items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs text-emerald-700 animate-muse-line-in">
              <Check size={11} className="shrink-0" />
              <span className="truncate">{handledText(item)} : retiré{item.subject ? ` (${item.subject})` : ''}</span>
            </span>
          ))}
        </div>
      )}
      {!data.connected ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-3 text-sm text-slate-500">
          <p className="min-w-0 flex-1">Nexus peut lire tes nouveaux mails, reçus et envoyés, et te proposer quoi en faire : une tâche, un point à aborder avec le client, une note de journal, une tâche à cocher. Lecture seule, aucun mail n'est conservé.</p>
          <button type="button" onClick={connect} className="shrink-0 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700">
            Relier ma boîte Gmail
          </button>
        </div>
      ) : data.mails.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-3 text-sm text-slate-400">
          Rien à faire de tes derniers mails{scanning ? '…' : '.'}
        </p>
      ) : (
        <>
          {toDo.length > 0 ? (
            <div className="gap-4 md:columns-2 2xl:columns-3">{toDo.map((mail) => item(mail, false))}</div>
          ) : (
            <p className="mb-3 rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-3 text-sm text-slate-400">
              Rien à traiter dans tes derniers mails.
            </p>
          )}
          {forInfo.length > 0 && (
            <div>
              <button type="button" onClick={toggleInfo} aria-expanded={infoOpen}
                className="mb-2 flex items-center gap-1.5 rounded-lg px-1 py-0.5 text-xs font-semibold text-slate-500 hover:text-slate-700">
                <ChevronDown size={13} className={`transition-transform ${infoOpen ? '' : '-rotate-90'}`} />
                Pour info <span className="rounded-full bg-slate-200/70 px-1.5 font-medium text-slate-500">{forInfo.length}</span>
                {!infoOpen && <span className="hidden truncate font-normal text-slate-400 sm:inline">· {forInfo.slice(0, 3).map((m) => m.from_name).join(', ')}{forInfo.length > 3 ? '…' : ''}</span>}
              </button>
              {infoOpen && <div className="gap-4 md:columns-2 2xl:columns-3">{forInfo.map((mail) => item(mail, true))}</div>}
            </div>
          )}
        </>
      )}
      {notice && <p className="mt-2 px-1 text-xs text-emerald-600">{notice}</p>}
      {error && <p className="mt-2 px-1 text-xs text-rose-600">{error}</p>}
    </section>
  );
};

export default MailCard;
